import { FlagsmithClientProvider } from "@openfeature/flagsmith-client-provider";
import { OpenFeature } from "@openfeature/web-sdk";

let flagsmithProvider: FlagsmithClientProvider | null = null;
let setupInFlight: Promise<FlagsmithClientProvider> | null = null;

/**
 * Initialises the Flagsmith provider, at most once. `App` and the context
 * provider's `identify()` both race for it, so both get the same in-flight
 * promise — otherwise the loser reached for a provider that did not exist yet
 * and pinned every flag to its default for the session.
 */
export const setupFeatureFlags = async (): Promise<FlagsmithClientProvider> => {
  if (setupInFlight) {
    return setupInFlight;
  }

  setupInFlight = (async () => {
    const provider = new FlagsmithClientProvider({
      environmentID: import.meta.env.VITE_FLAGSMITH_ENV_ID,
      cacheFlags: true,
      cacheOptions: {
        ttl: 60000 * 60 // Cache for 60 minutes
      },
      enableAnalytics: false
    });

    await OpenFeature.setProviderAndWait(provider);
    flagsmithProvider = provider;

    return provider;
  })();

  try {
    return await setupInFlight;
  } catch (e) {
    // A failed init must not poison every later attempt: drop the rejected
    // promise so the next caller retries rather than re-awaiting the failure.
    setupInFlight = null;
    throw e;
  }
};

export const getFlagsmithClient = () => {
  if (!flagsmithProvider) {
    throw new Error("Flagsmith provider not initialized");
  }

  const client = (flagsmithProvider as any)._client;

  if (!client) {
    throw new Error("Could not access Flagsmith client from provider");
  }

  return client;
};
